
import React, { createContext, useContext, useState, useEffect } from "react";
import enTranslations from "../locales/en.json";
import hiTranslations from "../locales/hi.json";
import esTranslations from "../locales/es.json";

export type Language = "en" | "hi" | "es";

type LanguageContextType = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  setLanguage: () => {},
  t: (key: string) => key,
});

const translations: Record<Language, Record<string, string>> = {
  en: enTranslations,
  hi: hiTranslations,
  es: esTranslations
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>("en");

  const t = (key: string): string => {
    // First try to get translation in selected language
    const translation = translations[language][key];
    // If not found, fallback to English
    if (!translation && language !== "en") {
      return translations["en"][key] || key;
    }
    return translation || key;
  };

  // Update HTML lang attribute when language changes
  useEffect(() => {
    document.documentElement.setAttribute("lang", language);
  }, [language]);

  // Load language preference from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem("portfolio-language") as Language | null;
    if (savedLanguage && ["en", "hi", "es"].includes(savedLanguage)) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("portfolio-language", language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
